import { useCallback, useEffect, useRef, useState } from "react";
import { loadFacebookSdk } from "./facebookSdk";
import {
  FACEBOOK_MESSAGE_ORIGINS,
  defaultEmbeddedSignupExtras,
  parseEmbeddedSignupMessage,
} from "./embeddedSignupSession";

/**
 * Embedded Signup: SDK load, postMessage session, FB.login code.
 */
export function useWhatsAppEmbeddedSignup({
  appId,
  configId,
  graphVersion = "v25.0",
  enabled,
}) {
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState("");
  const [sessionInfo, setSessionInfo] = useState(null);
  const [authCode, setAuthCode] = useState(null);
  const [loginError, setLoginError] = useState("");
  const fbRef = useRef(null);

  useEffect(() => {
    if (!enabled || !appId) {
      return undefined;
    }
    let cancelled = false;
    loadFacebookSdk(appId, graphVersion)
      .then((FB) => {
        if (!cancelled) {
          fbRef.current = FB;
          setSdkReady(true);
          setSdkError("");
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setSdkError(e.message || "Could not load Facebook SDK");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, appId, graphVersion]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const handler = (event) => {
      if (!FACEBOOK_MESSAGE_ORIGINS.includes(event.origin)) {
        return;
      }
      const info = parseEmbeddedSignupMessage(event.data);
      if (info && (info.wabaId || info.phoneNumberId || info.businessId || info.isCoexistence)) {
        setSessionInfo((prev) => ({ ...prev, ...info }));
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [enabled]);

  const launchEmbeddedSignup = useCallback(() => {
    setLoginError("");
    const FB = fbRef.current;
    if (!FB || !configId) {
      setLoginError("Facebook SDK or config ID is not ready");
      return;
    }
    const extras = defaultEmbeddedSignupExtras();
    FB.login(
      (response) => {
        if (response.authResponse && response.authResponse.code) {
          setAuthCode(response.authResponse.code);
        } else if (response.error) {
          setLoginError(response.error.message || "Facebook login failed");
        } else {
          setLoginError(
            "No authorization code returned. Complete the Facebook flow or try again."
          );
        }
      },
      {
        config_id: configId,
        response_type: "code",
        override_default_response_type: true,
        extras,
      }
    );
  }, [configId]);

  const resetFlow = useCallback(() => {
    setSessionInfo(null);
    setAuthCode(null);
    setLoginError("");
  }, []);

  return {
    sdkReady,
    sdkError,
    sessionInfo,
    authCode,
    loginError,
    launchEmbeddedSignup,
    resetFlow,
  };
}
